/**
 * Contractor Company Portal - Performance Page
 * 
 * Company performance metrics and trends.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Loader2,
  Building2,
  RefreshCw,
  LogOut,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  Clock,
  Percent,
  PoundSterling,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('contractor_portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============== COMPONENTS ==============

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, variant = 'default' }) => {
  const variants = {
    default: 'border-slate-700 bg-slate-800',
    success: 'border-emerald-500/30 bg-emerald-500/10',
    warning: 'border-amber-500/30 bg-amber-500/10',
    danger: 'border-red-500/30 bg-red-500/10',
  };

  return (
    <Card className={variants[variant]}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {trendValue}
              </div>
            )}
          </div>
          {Icon && (
            <div className={`p-3 rounded-xl ${variant === 'success' ? 'bg-emerald-500/20' : variant === 'warning' ? 'bg-amber-500/20' : variant === 'danger' ? 'bg-red-500/20' : 'bg-slate-700'}`}>
              <Icon className={`w-6 h-6 ${variant === 'success' ? 'text-emerald-400' : variant === 'warning' ? 'text-amber-400' : variant === 'danger' ? 'text-red-400' : 'text-slate-400'}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const TrendChart = ({ data, title }) => {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <Card className="border-slate-700 bg-slate-800">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-slate-400">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1 h-32">
          {data.slice(-12).map((item, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center">
              <div 
                className="w-full bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t"
                style={{ height: `${Math.max((item.value / maxValue) * 100, 5)}%` }}
              />
              <span className="text-[10px] text-slate-500 mt-1 truncate w-full text-center" title={item.key}>
                {item.key?.slice(-2)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============== MAIN COMPONENT ==============

export default function ContractorCompanyPerformance() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);

  const contractorName = localStorage.getItem('contractor_portal_name') || 'Contractor Portal';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/contractor-company/performance');
      setPerformance(res.data.data);
    } catch (err) {
      console.error('Load error:', err);
      if (err.response?.status === 401) {
        navigate('/contractor-company/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('contractor_portal_token');
    if (!token) {
      navigate('/contractor-company/login');
      return;
    }
    loadData();
  }, [loadData, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('contractor_portal_token');
    localStorage.removeItem('contractor_portal_id');
    localStorage.removeItem('contractor_portal_name');
    navigate('/contractor-company/login');
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const summary = performance?.summary || {};
  const financials = performance?.financials || {};
  const trends = performance?.trends || {};

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{contractorName}</h1>
              <p className="text-xs text-slate-400">Performance Metrics</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadData} disabled={loading} className="text-slate-300 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <Link 
              to="/contractor-company/dashboard" 
              className="px-4 py-3 text-sm font-medium text-slate-400 hover:text-white border-b-2 border-transparent"
            >
              Dashboard
            </Link>
            <Link 
              to="/contractor-company/jobs" 
              className="px-4 py-3 text-sm font-medium text-slate-400 hover:text-white border-b-2 border-transparent"
            >
              Jobs
            </Link>
            <Link 
              to="/contractor-company/performance" 
              className="px-4 py-3 text-sm font-medium text-blue-400 border-b-2 border-blue-400"
            >
              Performance
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Job Metrics */}
        <h2 className="text-lg font-semibold text-white mb-4">Job Performance</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title="Total Jobs"
            value={summary.total_jobs || 0}
            icon={Clock}
          />
          <MetricCard
            title="Completed"
            value={summary.completed_jobs || 0}
            subtitle={`${summary.completion_rate || 0}% completion rate`}
            icon={CheckCircle}
            variant="success"
          />
          <MetricCard
            title="Overdue"
            value={summary.overdue_jobs || 0}
            icon={AlertTriangle}
            variant={summary.overdue_jobs > 0 ? 'danger' : 'success'}
          />
          <MetricCard
            title="SLA Compliance"
            value={`${summary.sla_compliance || 0}%`}
            icon={Percent}
            variant={summary.sla_compliance >= 90 ? 'success' : summary.sla_compliance >= 70 ? 'warning' : 'danger'}
          />
        </div>

        {/* Financial Metrics */}
        <h2 className="text-lg font-semibold text-white mb-4">Financial Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <MetricCard
            title="Total Invoiced"
            value={formatCurrency(financials.total_invoiced)}
            icon={PoundSterling}
          />
          <MetricCard
            title="Total Approved"
            value={formatCurrency(financials.total_approved)}
            icon={CheckCircle}
            variant="success"
          />
          <MetricCard
            title="Avg Invoice Variance"
            value={`${financials.avg_variance_percent || 0}%`}
            subtitle={financials.avg_variance_percent > 5 ? 'Over quote average' : financials.avg_variance_percent < -5 ? 'Under quote average' : 'Within tolerance'}
            icon={TrendingUp}
            variant={Math.abs(financials.avg_variance_percent || 0) > 10 ? 'warning' : 'success'}
          />
        </div>

        {/* Trends */}
        <h2 className="text-lg font-semibold text-white mb-4">Trends</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TrendChart data={trends.jobs_by_month} title="Jobs by Month" />
          
          <Card className="border-slate-700 bg-slate-800">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-400">Performance Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Completion Rate</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${summary.completion_rate || 0}%` }}
                      />
                    </div>
                    <span className="text-white text-sm">{summary.completion_rate || 0}%</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">SLA Compliance</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${summary.sla_compliance >= 90 ? 'bg-emerald-500' : summary.sla_compliance >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${summary.sla_compliance || 0}%` }}
                      />
                    </div>
                    <span className="text-white text-sm">{summary.sla_compliance || 0}%</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-slate-700">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Active Jobs</span>
                    <span className="text-white">{summary.total_jobs - summary.completed_jobs || 0}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
